/**
 * Open scoreboard scorecard (layout duplicated from DateMemories/Scorecard).
 * Expects route params :scoreboard_id and :result_id (nested show API).
 * Data: GET /api/v1/scoreboards/:scoreboard_id/scoreboard_results/:result_id
 */
import React, { useMemo, useRef } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/CloseRounded'
import CalendarIcon from '@mui/icons-material/CalendarMonthRounded'
import ImageDownloadIcon from '@mui/icons-material/FileDownloadRounded'
import styled from 'styled-components'
import { DateTime } from 'luxon'
import { useNavigate, useParams } from 'react-router-dom'
import { BackdropWrapper } from '../../Dashboard/DateRequests/DateRequests.styles'
import { ActivityBackDrop } from '../../Dashboard/PlanActivity/CreateActivity/CreateActivity.styles'
import Text from '../../../components/Text'
import TextWithIcon from '../../../components/TextWithIcon'
import { colors } from '../../../styles/_var'
import type { OpenScoreboardData, TeamScoreboardData } from '../../../services/apiTypes'
import { useFetchScoreboardResultQuery } from '../../../services/api'
import type { ScoreboardParticipant, User } from '../../../utils/types'
import {
  exportAsImage,
  extractWinnerParticipantId,
  getUserByScoreboardParticipantId,
  moveToFront,
  scoresDataToStringMap,
} from '../../../utils/helpers'
import OpenScoreboardResult from '../../../utils/openScoreboardResult'
import { useHideFooterOnMount } from '../../../hooks/hooks'
import { useUiStore } from '../../../stores/uiStore'
import ScoreboardBanner from '../../../assets/images/scoreboard_banner.jpg'
import ParticipantScore from './ParticipantScore'
import WinnerCard from './WinnerCard'

const Scorecard = () => {
  const { scoreboard_id, result_id } = useParams<{
    scoreboard_id: string
    result_id: string
  }>()
  useHideFooterOnMount()
  const sid = Number(scoreboard_id)
  const rid = Number(result_id)
  const skip = !Number.isFinite(sid) || !Number.isFinite(rid) || sid <= 0 || rid <= 0

  const { data, isLoading, isError } = useFetchScoreboardResultQuery(
    { scoreboard_id: sid, result_id: rid },
    { skip }
  )

  if (skip) {
    return (
      <Wrapper>
        <Text center mx={2} my={2}>
          Missing scoreboard_id or result_id in the URL.
        </Text>
      </Wrapper>
    )
  }

  if (isLoading) {
    return (
      <Wrapper>
        <Text center mx={2} my={2}>
          Loading…
        </Text>
      </Wrapper>
    )
  }

  if (isError || !data?.payload || !data.scoreboard) {
    return (
      <Wrapper>
        <Text center mx={2} my={2}>
          Could not load scorecard.
        </Text>
      </Wrapper>
    )
  }

  return (
    <ScorecardContent
      result={data.payload}
      scoreboardTitle={data.scoreboard.title}
      scoreboardDescription={data.scoreboard.description}
      participants={data.scoreboard.scoreboard_participants || []}
      endLabel={data.payload.created_at}
    />
  )
}

type ScorecardContentProps = {
  result: OpenScoreboardData
  scoreboardTitle: string
  scoreboardDescription?: string | null
  participants: ScoreboardParticipant[]
  endLabel?: string
}

const ScorecardContent = ({
  result,
  scoreboardTitle,
  scoreboardDescription,
  participants,
  endLabel,
}: ScorecardContentProps) => {
  const ref = useRef<HTMLElement>(null)
  const navigate = useNavigate()
  const { openSuccessToast } = useUiStore()

  const raw = result.scores_data as Record<string, unknown> | undefined
  const isTeam = !!(raw && typeof raw === 'object' && 'teams' in raw && 'members' in raw && 'results' in raw)

  const teamScoresData = isTeam ? (raw as unknown as TeamScoreboardData) : null
  const soloScoresRaw = !isTeam
    ? (raw as Record<string, (number | string)[]> | undefined)
    : undefined

  const scores = useMemo(() => {
    if (isTeam && teamScoresData?.results) {
      return scoresDataToStringMap(teamScoresData.results as Record<string, (number | string)[]>)
    }
    return scoresDataToStringMap(soloScoresRaw)
  }, [isTeam, teamScoresData, soloScoresRaw])

  const deducedWinner = useMemo(() => {
    if (isTeam || !participants.length) return null
    if (extractWinnerParticipantId(result) != null) return null
    const inst = new OpenScoreboardResult(participants, result)
    return inst.deduceWinner()
  }, [isTeam, participants, result])

  const winnerFromPayload = extractWinnerParticipantId(result)
  const winner = isTeam
    ? ((teamScoresData?.winning_team as string | number | null) ?? null)
    : winnerFromPayload ?? (deducedWinner != null ? Number(deducedWinner) : null)

  const downloadScorecard = async (element: HTMLElement | null, filename: string) => {
    if (!element) return
    openSuccessToast({ message: 'Downloading scorecard...' })
    return exportAsImage(element, filename)
  }

  const dateLine =
    endLabel != null
      ? DateTime.fromISO(endLabel).toLocaleString(DateTime.DATE_FULL)
      : ''

  return (
    <Wrapper ref={ref}>
      <BackdropWrapper>
        <ActivityBackDrop backdrop={ScoreboardBanner} />
        <ActionButton width="100%" display="flex" justifyContent="space-between">
          <IconButton sx={{ position: 'relative' }} onClick={() => navigate(-1)}>
            <CloseIcon fontSize="large" />
          </IconButton>
          <IconButton
            sx={{ position: 'relative' }}
            onClick={() => {
              downloadScorecard(ref.current, scoreboardTitle.replace(/ /g, '-')).then(() => {
                navigate(-1)
              })
            }}
          >
            <ImageDownloadIcon fontSize="large" />
          </IconButton>
        </ActionButton>
      </BackdropWrapper>
      <ScorecardInfo elevation={4}>
        <Box p={2}>
          <Text variant="semi-large" weight="semibold" center mb={1}>
            {scoreboardTitle}
          </Text>
          {!!scoreboardDescription && (
            <Text variant="small" center mb={1} lighten>
              {scoreboardDescription}
            </Text>
          )}
          {!!dateLine && (
            <TextWithIcon
              justifyContent="center"
              icon={<CalendarIcon fontSize="large" />}
              variant="medium"
              lineHeight={1}
            >
              {dateLine}
            </TextWithIcon>
          )}
        </Box>
      </ScorecardInfo>
      {isTeam && teamScoresData ? (
        <TeamScorecardContent
          scores={scores}
          teamScoresData={teamScoresData}
          participants={participants}
          winner={winner as string | number | null}
        />
      ) : (
        <SoloScorecardContent
          scores={scores}
          participants={participants}
          winner={winner as number | null}
        />
      )}
      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
        mb={2}
      >
        <Text variant="extra-small" weight="semibold" center>
          Powered by
        </Text>
        <img
          src="https://gusiberi-dev.s3.eu-west-1.amazonaws.com/gusiberi-logo.png"
          height="25px"
          width="fit-content"
          alt="gusiberi logo"
        />
      </Box>
    </Wrapper>
  )
}

const TeamScorecardContent = ({
  scores,
  teamScoresData,
  participants,
  winner,
}: {
  scores: Record<string, string[]>
  teamScoresData: TeamScoreboardData
  participants: ScoreboardParticipant[]
  winner: string | number | null
}) => {
  if (winner == null || winner === '') return null
  const winnerKey = String(winner)
  const winningTeamMembers = teamScoresData.members[winnerKey]
  const winningTeamMembersUser = winningTeamMembers
    ?.map((pid) => getUserByScoreboardParticipantId(pid, participants))
    .filter(Boolean) as User[]

  const teamsAndScores = moveToFront(
    Object.keys(scores).map((teamName) => ({
      team: {
        teamName,
        members: (teamScoresData.members[teamName] || [])
          .map((pid) => getUserByScoreboardParticipantId(pid, participants))
          .filter(Boolean) as User[],
      },
      scores: scores[teamName],
      isWinner: winner === teamName,
    })),
    (item) => item.isWinner
  )

  return (
    <ScoreboardContent>
      <WinnerCard user={winningTeamMembersUser} teamName={`${winner}`} scores={[]} />
      <Box mt={1.2}>
        {teamsAndScores.map((teamData, index) => (
          <ParticipantScore
            index={index + 1}
            isWinner={teamData.isWinner}
            key={`${teamData.team.teamName}-${index}`}
            team={teamData.team}
            scores={teamData.scores}
            totalScore={teamData.scores.reduce((acc, curr) => acc + parseInt(curr, 10), 0)}
          />
        ))}
      </Box>
    </ScoreboardContent>
  )
}

const SoloScorecardContent = ({
  scores,
  participants,
  winner,
}: {
  scores: Record<string, string[]>
  participants: ScoreboardParticipant[]
  winner: number | null
}) => {
  if (winner == null || typeof winner === 'string') return null
  const winnerKey = String(winner)
  const winnerScore = scores[winnerKey]
  const winnerParticipant = getUserByScoreboardParticipantId(winner, participants)

  const participantsAndScores = moveToFront(
    Object.keys(scores)
      .map((pid) => ({
        pid,
        user: getUserByScoreboardParticipantId(parseInt(pid, 10), participants),
        totalScore: scores[pid].reduce((acc, curr) => acc + parseInt(curr, 10), 0),
        scores: scores[pid],
      }))
      .sort((a, b) => b.totalScore - a.totalScore)
      .slice(0, 5),
    (item) => parseInt(item.pid, 10) === winner
  )

  return (
    <ScoreboardContent>
      {!!winnerParticipant && (
        <WinnerCard user={winnerParticipant} scores={winnerScore || []} />
      )}
      <Box mt={1.2}>
        {participantsAndScores.map((participant, index) => (
          <ParticipantScore
            index={index + 1}
            isWinner={winner === parseInt(participant.pid, 10)}
            key={participant.pid}
            user={participant.user ?? undefined}
            totalScore={participant.totalScore}
            scores={participant.scores}
          />
        ))}
      </Box>
    </ScoreboardContent>
  )
}

export default Scorecard

const ScoreboardContent = styled(Box)`
  margin-top: -15px;
`

const Wrapper = styled(Box)`
  max-width: 450px;
  background-color: ${colors.lightgrey};
  position: fixed;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  overflow: scroll;
`

const ScorecardInfo = styled(Paper)`
  width: 70%;
  margin-left: auto;
  margin-right: auto;
  transform: translateY(-50%);

  &.MuiPaper-root {
    background: rgba(255, 255, 255, 0.92);
  }
`

const ActionButton = styled(Box)`
  position: absolute;
  top: 0;
  & button {
    position: relative;
    margin-top: 5px;

    &:first-child {
      margin-left: 5px;
    }

    &:last-child {
      margin-right: 5px;
    }
  }
`
